/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { LoggingWindow } from "./LoggingWindow";

/**
 * Single runtime owner of the current Whiny logging window.
 *
 * The window is written only by the config-apply / restore paths.
 * {@link isWhinyEnabledNow} re-evaluates the window against the current
 * wall-clock time on every call, so Whiny self-expires at the window
 * edges without any further apply — an expired or not-yet-started
 * window reads as off.
 */
export class WhinyStateProvider {
  private static readonly MS_PER_SECOND: number = 1000;

  private static sWindow: LoggingWindow | null = null;

  /**
   * Sets the active window. A null window disables Whiny.
   *
   * @param window - The window to honour, or null to disable
   */
  public static setWindow(window: LoggingWindow | null): void {
    WhinyStateProvider.sWindow = window;
  }

  /**
   * Whether Whiny is enabled at the current wall-clock time.
   * Re-evaluates on every call; never caches a boolean.
   *
   * @returns true when a window is set and the current time is inside it
   */
  public static isWhinyEnabledNow(): boolean {
    if (WhinyStateProvider.sWindow === null) {
      return false;
    }

    const nowEpochSec: number = Math.floor(
      Date.now() / WhinyStateProvider.MS_PER_SECOND
    );
    return WhinyStateProvider.sWindow.isInWindow(nowEpochSec);
  }
}
